import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Skeleton,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ShoppingCart, FileText, CheckCircle, LockOpen, Heart, ClipboardPlus, Map } from "lucide-react";
import { useNavigation } from "../../../hooks/useNavigation";
import { resolvePharmacyId } from "../pharmacyCoverage/coverageData";
import { PharmacyCoverageNavState } from "../pharmacyCoverage/PharmacyShipToCoverage";
import CustomCard from "../../common/Card";
import CustomTooltip from "../../common/CustomTooltip";
import { USER_ROLES, PATIENT_LIST_ACTIONS, PRODUCT_CATEGORY } from "../../../constants/applicationConstants";
import { selectIsTokenAvailable, selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { Cancel } from "@mui/icons-material";
import WarningPopupNew, { WarningPopupNewObjectProps } from "../../common/WarningPopupNew";
import CompletePracticeRegistrationModal from "../account/CompletePracticeRegistrationModal";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import ProductDocumentsModal from "./ProductDocumentsModal";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchProductDocumentsByProductId } from "../../../redux/slice/product/createProductSlice";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";
import type { SingleAlertInfo } from "../../../redux/slice/commonSlice";
import ConfirmationActionModal from "../../common/ConfirmationActionModal";
import type { ProductConfigRules } from "../../../service/ProductService";
import {
  getExploreMarketProductActionFlags,
  shouldShowExploreMarketListPrice,
} from "../../../utils/exploreMarketProductActions";
import { priceFormatter } from "../../../utils/Utility";

export interface MarketCardProps {
  id: string;

  title: string;
  company: string;

  composition?: string;
  price?: number;
  listPrice?: number | null;
  productCategory?: string|null;
  isFavorite?: boolean;
  agreementRequired?: boolean;
  agreementRequested?: boolean;
  agreementApproved?: boolean;
  agreementStatus?: string | null;
  productStatus?: string | null;

  loading?: boolean;
  disabled?: boolean;

  viewMode?: "grid" | "list";

  skuAllowablePrices?: any[];
  macPrices?: any[];
  extendedPrice?: number | null;

  onFavoriteClick?: (id: string) => void;
  onRequestAgreement?: (id: string) => void;
  onOrderProduct?: (id: string) => void;
  onClick?: (id: string) => void;
  onProductClick?: (id: string) => void;
  productConfigs?: ProductConfigRules[];
  manufacturerId?: number | null;
}

const MarketCard: React.FC<MarketCardProps> = ({
  id,
  title,
  company,
  composition,

  price,
  listPrice = null,
  productCategory,
  isFavorite = false,
  agreementRequired = false,
  agreementRequested = false,
  agreementApproved = false,
  agreementStatus = null,
  productStatus = null,

  loading = false,
  disabled = false,

  viewMode = "grid",

  skuAllowablePrices = [],
  macPrices = [],
  extendedPrice,

  onFavoriteClick,
  onRequestAgreement,
  onOrderProduct,
  onClick,
  onProductClick,
  productConfigs = [],
  manufacturerId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { userInfo } = useAuthSlice();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isRestrictedUser = userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU;
  // CS-1732: Hide Order button for users without CREATE_ORDER permission
  const canCreateOrder = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessPatientActions", PATIENT_LIST_ACTIONS.CREATE_ORDER)(state));
  // CS-1732: Hide Request Agreement button based on signing permissions
  const canPracticeSignAgreement = useSelector((state: RootState) => selectIsTokenAvailable("canPracticeSignAgreement")(state));
  const canRequestAgreement = canPracticeSignAgreement || isRestrictedUser;
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const handleViewCoverage = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/pharmacy_coverage", {
      state: {
        compoundType: "sterile",
        pharmacyFilter: resolvePharmacyId(company, manufacturerId),
        productScoped: true,
      } as PharmacyCoverageNavState,
    });
  };
  const [showPRUWarning, setShowPRUWarning] = useState<WarningPopupNewObjectProps>({ show: false, message: '' });
  const [showAgreementConfirm, setShowAgreementConfirm] = useState(false);
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  // CS-1728: Product Documents modal state
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [prefetchedDocs, setPrefetchedDocs] = useState<any[] | undefined>(undefined);

  const handleViewDocuments = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await dispatch(fetchProductDocumentsByProductId(Number(id)));
    if (result.meta.requestStatus === "fulfilled") {
      const docs: any[] = (result.payload?.data ?? []).filter(
        (pd: any) => pd.entityName === "Document" && pd.docName !== null
      );
      if (docs.length === 0) {
        const alertObj: SingleAlertInfo = { message: "No documents found for this product.", alertType: "error" };
        dispatch(setSingleAlertObj(alertObj));
        return;
      }
      setPrefetchedDocs(docs);
      setShowDocumentsModal(true);
    }
  };
  
  const withPRUGuard = (fn: () => void) => () => {
      if (isRestrictedUser) {
        setShowPRUWarning({
          show: true,
          title: "Pre Registered User Warning",
          message: (
            <center>
              You don't have access to this feature. Please complete the practice
              registration in order to use the full system functionality.
            </center>
          ),
          variant: "warning",
          buttons: [
            {
              name: "Cancel",
              color: "error",
              icon: <Cancel sx={{ fontSize: "1rem" }} />,
              onClick: () => setShowPRUWarning({ show: false, message: '' }),
            },
            ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
            ? [
                {
                  name: "Complete Registration",
                  color: "success" as const,
                   onClick: () => {
                    setRegistrationModalOpen(true);
                    setShowPRUWarning({ show: false, message: "" });
                  },
                },
              ]
            : []),
          ],
        });
        return;
      }
      fn();
    };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    withPRUGuard(() => onFavoriteClick?.(id))();
  };

  const handleAgreement = (e: React.MouseEvent) => {
    e.stopPropagation();
    // CS-1741: Confirm Request Agreement for non-PRU users before API call.
    if (isRestrictedUser) {
      withPRUGuard(() => onRequestAgreement?.(id))();
      return;
    }
    setShowAgreementConfirm(true);
  };

  const handleOrder = (e: React.MouseEvent) => {
    e.stopPropagation();
    withPRUGuard(() => onOrderProduct?.(id))();
  };

  const handleCardClick = () => {
    if (!disabled) {
      onProductClick?.(id);
      onClick?.(id);
    }
  };

  // Theme-aware colors
  const borderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.1)";
  const cardBg = isDark ? "rgba(20,24,33,0.6)" : "rgba(255,255,255,0.82)";
  const tagTeal = "#14b8a6";
  const restShadow = isDark ? "0 4px 16px rgba(0,0,0,0.2)" : "0 4px 14px rgba(15,23,42,0.06)";
  const hoverShadow = isDark
    ? "0 14px 32px rgba(0,0,0,0.42)"
    : "0 14px 28px rgba(15,23,42,0.12)";
  const hoverBorderColor = isDark ? "rgba(20,184,166,0.4)" : "rgba(20,184,166,0.35)";

  const primaryActionLabelColor = isDark ? "#fff" : "#0f766e";
  const primaryActionBtnSx = {
    borderRadius: "10px",
    textTransform: "none" as const,
    fontWeight: 700,
    color: primaryActionLabelColor,
    backgroundColor: isDark ? "transparent" : "rgba(15,118,110,0.08)",
    border: `1px solid ${isDark ? "rgba(4, 139, 124, 0.32)" : "rgba(15,118,110,0.45)"}`,
    transition: "background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
    "& .MuiButton-startIcon, & .MuiButton-startIcon svg": {
      color: primaryActionLabelColor,
    },
    "&:hover": {
      backgroundColor: isDark ? tagTeal : "#0f766e",
      color: "#fff",
      borderColor: isDark ? tagTeal : "#0f766e",
      boxShadow: `0 6px 16px ${isDark ? "rgba(20,184,166,0.3)" : "rgba(15,118,110,0.28)"}`,
      "& .MuiButton-startIcon, & .MuiButton-startIcon svg": {
        color: "#fff",
      },
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(148,163,184,0.5)" : "rgba(71,85,105,0.72)",
      backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
      borderColor: isDark ? "rgba(148,163,184,0.16)" : "rgba(100,116,139,0.28)",
      "& .MuiButton-startIcon, & .MuiButton-startIcon svg": {
        color: isDark ? "rgba(148,163,184,0.5)" : "rgba(71,85,105,0.72)",
      },
    },
  };

    const secondaryActionBtnSx = {
    borderRadius: "10px",
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.82rem",
    color: isDark ? "#2dd4bf" : "#0d9488",
    border: `1px solid ${isDark ? "rgba(45,212,191,0.3)" : "rgba(13,148,136,0.28)"}`,
    transition: "background-color 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      backgroundColor: isDark ? "rgba(45,212,191,0.14)" : "rgba(13,148,136,0.1)",
      borderColor: isDark ? "#2dd4bf" : "#0d9488",
    },
  };

  // Derived states — shared with Product Management via getExploreMarketProductActionFlags
  const {
    isAgreementFinished,
    showAgreementButton,
    shouldShowRequestRx,
    showOrderButton,
  } = getExploreMarketProductActionFlags({
    productCategory,
    agreementRequired,
    agreementStatus,
    agreementApproved,
    productStatus,
    productConfigs,
    canCreateOrder,
    canRequestAgreement,
  });
  const isAgreementRequestedPending =
    agreementRequired && !agreementStatus && agreementRequested;

  const hasSkuRates = skuAllowablePrices.length > 0;
  const hasMacRates = macPrices.length > 0;
  const showListPriceLabel = shouldShowExploreMarketListPrice(composition, productCategory);
  const referencePriceLabel = showListPriceLabel ? "List Price" : "Allowable";
  const catalogPrice =
    showListPriceLabel && listPrice != null ? listPrice : price;
  const listActionButtonWidth = { xs: 60, sm: 70, md: 80, lg: 110 };

  const getAgreementLabel = (short = false) => {
    if (!agreementRequired) return "Order Product";
    if (isAgreementRequestedPending) return short ? "Requested" : "Agreement Requested";
    if (!agreementStatus) return short ? "Agreement" : "Request Agreement";
    return short ? "Requested" : "Agreement Requested";
  };

  const getAgreementChip = () => {
    const status = agreementStatus?.toUpperCase();

    if (!agreementRequired) {
      if (status === "EXECUTED" && productCategory !== PRODUCT_CATEGORY.STANDARD && productCategory !== PRODUCT_CATEGORY.GENERAL) {
        return { label: "Agreement Signed", color: "#22c55e", icon: <CheckCircle size={10} /> };
      } else if (status === "EXECUTED" && (productCategory === PRODUCT_CATEGORY.STANDARD || productCategory === PRODUCT_CATEGORY.GENERAL)) {
        return { label: "Open Access", color: "#22c55e", icon: <LockOpen size={10} /> };
      }else{
        return null;
      }
    }

    if (isAgreementRequestedPending) {
      return { label: "Agreement Requested", color: "#f59e0b", icon: <FileText size={10} /> };
    }

    if (!status) {
      return { label: "Agreement Required", color: "#f59e0b", icon: <FileText size={10} /> };
    }

    switch (status) {
      case "HELD":
        return { label: "Agreement In Progress", color: "#f59e0b", icon: <FileText size={10} /> };
      case "INITIATED":
        return { label: "Agreement In Progress", color: "#f59e0b", icon: <FileText size={10} /> };
      case "PRACTICE SIGNED":
        return { label: "Agreement In Progress", color: "#f59e0b", icon: <FileText size={10} /> };
      case "EXECUTED":
        return { label: "Agreement Signed", color: "#22c55e", icon: <CheckCircle size={10} /> };
      default:
        return { label: "Agreement Requested", color: "#f59e0b", icon: <FileText size={10} /> };
    }
  };

  const isAgreementDisabled = !!agreementStatus || isAgreementRequestedPending || disabled;
  const agreementBtnColor = isDark ? "#fb923c" : "#ea580c";
  const agreementTooltipTitle = getAgreementLabel();
  const favoriteTooltipTitle = isFavorite ? "Remove from favorites" : "Add to favorites";
  const orderTooltipTitle = "Order Product";
  const comingSoonTooltipTitle = "Request Rx";

  const compactBtnSx = {
    fontSize: { xs: 11, md: 12 },
    whiteSpace: "nowrap" as const,
    py: { xs: 0.3, md: 0.8 },
    minHeight: { xs: 30, md: 36 },
  };

  const renderAgreementButton = (short = false) => (
    <CustomTooltip title={agreementTooltipTitle} placement="top">
      <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
        <Button
          size="small"
          startIcon={!isSmallMobile && <FileText size={14} />}
          disabled={isAgreementDisabled}
          onClick={handleAgreement}
          fullWidth
          sx={{
            ...compactBtnSx,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            color: agreementBtnColor,
            backgroundColor: isDark ? `${agreementBtnColor}22` : `${agreementBtnColor}14`,
            border: isAgreementDisabled ? "1px solid #8A99A8" : `1px solid ${agreementBtnColor}66`,
            transition: "background-color 0.18s ease",
            "&:hover": {
              backgroundColor: agreementBtnColor,
              color: "#fff",
            },
          }}
        >
          {getAgreementLabel(short)}
        </Button>
      </Box>
    </CustomTooltip>
  );

  const renderOrderButton = (label: string) => (
    <CustomTooltip title={orderTooltipTitle} placement="top">
      <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
        <Button
          size="small"
          startIcon={!isSmallMobile && <ShoppingCart size={14} />}
          disabled={disabled}
          onClick={handleOrder}
          fullWidth
          sx={{ ...primaryActionBtnSx, ...compactBtnSx }}
        >
          {label}
        </Button>
      </Box>
    </CustomTooltip>
  );

  const renderComingSoonButton = (label: string) => (
    <CustomTooltip title={comingSoonTooltipTitle} placement="top">
      <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
        <Button
          size="small"
          disabled
          fullWidth
          sx={{
            ...compactBtnSx,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            color: isDark ? "#64748b" : "#94a3b8",
            backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.05)",
            border: "1px solid #8A99A8",
          }}
        >
          {label}
        </Button>
      </Box>
    </CustomTooltip>
  );

  const popups = (
    <>
      {showAgreementConfirm && (
        <ConfirmationActionModal
          open={showAgreementConfirm}
          onClose={() => setShowAgreementConfirm(false)}
          onConfirm={() => {
            onRequestAgreement?.(id);
            setShowAgreementConfirm(false);
          }}
          title="Confirmation"
          subtitle="Agreement request"
          message={
            <>
              Are you sure you want to request the agreement for <strong>{title}</strong>?
            </>
          }
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}
      {showPRUWarning.show && (
        <WarningPopupNew
          open={showPRUWarning.show}
          onClose={() => setShowPRUWarning({ show: false, message: '' })}
          title={showPRUWarning.title}
          message={<>{showPRUWarning.message}</>}
          variant={showPRUWarning.variant}
          buttons={showPRUWarning.buttons}
        />
      )}
      {registrationModalOpen && (
        <CompletePracticeRegistrationModal
          open={registrationModalOpen}
          onClose={() => setRegistrationModalOpen(false)}
        />
      )}
    </>
  );

  // ─── LIST VIEW ───────────────────────────────────────────────────────
  if (viewMode === "list") {
    return (
      <>
        {popups}
        <CustomCard
          onClick={handleCardClick}
          sx={{
            width: "100%",
            borderRadius: "14px",
            padding: { xs: "8px 10px !important", md: "16px 20px !important" },
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: { xs: 1, md: 3 },
            overflow: "hidden", // Prevent any internal overflow
            cursor: disabled && !loading ? "not-allowed" : "pointer",
            border: `1px solid ${borderColor}`,
            backgroundColor: cardBg,
            backgroundImage: "none",
            backdropFilter: "blur(10px)",
            boxShadow: restShadow,
            opacity: disabled && !loading ? 0.7 : 1,
            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
            "&:hover": {
              boxShadow: disabled ? restShadow : hoverShadow,
              borderColor: disabled ? borderColor : hoverBorderColor,
            },
          }}
        >
          {/* Logo Section */}
          {/* <Box
          sx={{
            backgroundColor: iconBgColor,
            borderRadius: 2,
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {loading ? (
            <Skeleton variant="circular" width={28} height={28} />
          ) : (
            <Package size={isMobile ? 24 : 28} color={subtitleColor} />
          )}
        </Box> */}

        {/* Info Section: Title, Company & Composition Tag */}
        <Box sx={{ 
          flex: 1, 
          minWidth: 0, 
          overflow: "hidden", 
          display: "flex", 
          flexDirection: "column",
          maxWidth: { xs: "200px", sm: "300px", md: "400px", lg: "500px" }
        }}>
          {/* Title Row with Composition */}
            <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, mb: 0.5 }}>
              {loading ? (
                <Skeleton width="60%" height={24} />
              ) : (
                <Typography
                  fontSize={{ xs: "13px", md: "16px" }}
                  fontWeight={700}
                  noWrap
                  color="text.primary"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    flex: "0 1 auto", // Changed from "1 1 auto" to "0 1 auto" to prevent shrinking
                    minWidth: "100px", // Reduced minimum width to allow space for composition
                    maxWidth: { xs: "100px", sm: "150px", md: "200px", lg: "250px" }
                  }}
                >
                  {title}
                </Typography>
              )}
              {/* Composition chip beside product name - responsive sizing */}
              {!loading && composition && (
                <CustomTooltip title={composition} placement="top" arrow>
                  <Box
                    sx={{
                      px: { xs: 0.6, sm: 0.8, md: 1.2 },
                      py: 0.4,
                      borderRadius: 4,
                      fontSize: { xs: 8, sm: 9, md: 10 },
                      fontWeight: 700,
                      backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.1)",
                      color: "#14b8a6",
                      border: `1px solid ${isDark ? "rgba(20,184,166,0.25)" : "rgba(20,184,166,0.2)"}`,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      width: "fit-content",
                      display: "block",
                    }}
                  >
                    {composition}
                  </Box>
                </CustomTooltip>
              )}
            </Box>

            {/* Company Row */}
            <Box display="flex" alignItems="center" sx={{ minWidth: 0, mb: 1 }}>
              {loading ? (
                <Skeleton width="40%" height={18} />
              ) : (
                <Typography
                  fontSize={{ xs: "11px", md: "12px" }}
                  sx={{
                    color: "#8A99A8",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    flex: 1,
                    minWidth: 0
                  }}
                  noWrap
                >
                  {company}
                </Typography>
              )}
            </Box>

          </Box>

          {/* Right Section: Status, Price, Action Button, Favorite */}
          <Box
            display="flex"
            alignItems="center"
            sx={{
              flexShrink: 0,
              gap: { xs: 0.3, sm: 0.5, md: 0.8, lg: 1.5 },
            ml: 'auto', // Ensure right alignment
            minWidth: { xs: "120px", sm: "200px", md: "400px", lg: "500px" }
          }}
        >
          {/* Mobile Only - Show Action Button */}
          {isMobile && (
            <Box sx={{ 
              width: listActionButtonWidth, 
              display: "flex", 
              justifyContent: "center",
              flexShrink: 0
            }}>
              {showAgreementButton ? (
                renderAgreementButton(true)
              ) : showOrderButton ? (
                renderOrderButton("Order")
              ) : shouldShowRequestRx ? (
                renderComingSoonButton("Request Rx")
              ) : null}
            </Box>
          )}

          {/* Desktop Only Data Columns */}
          {!isMobile && (
            <>
              {/* Status Tag Column */}
              <Box sx={{ 
                width: { xs: 80, sm: 100, md: 120, lg: 140 }, 
                display: "flex", 
                justifyContent: "center",
                flexShrink: 1
              }}>
                {!loading && (
                  <>
                    {(() => {
                      const chip = getAgreementChip();
                      if (!chip) return null;
                      return (
                        <CustomTooltip title={chip.label} placement="top" arrow>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.6,
                              px: 1.2,
                              py: 0.4,
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 700,
                              backgroundColor: isDark
                                ? `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.15)`
                                : `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.1)`,
                              color: chip.color,
                              border: `1px solid ${isDark
                                ? `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.25)`
                                : `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.2)`}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {chip.icon}
                            <span style={{ marginLeft: 4 }}>{chip.label.toUpperCase()}</span>
                          </Box>
                        </CustomTooltip>
                      );
                    })()}
                  </>
                )}
              </Box>

              {/* Price Column */}
              <Box display="flex" flexDirection="column" alignItems="center" sx={{ 
                width: { xs: 60, sm: 80, md: 100, lg: 120 }, 
                flexShrink: 1 
              }}>
                <Typography fontSize="10px" sx={{ color: "#8A99A8", mb: 0.2, fontWeight: 500, whiteSpace: "nowrap" }}>
                  {referencePriceLabel}
                </Typography>
                {loading ? (
                  <Skeleton width={40} height={20} />
                ) : (
                  <Box display="flex" alignItems="center" gap={0.8} sx={{ flexWrap: "nowrap" }}>
                    {/* Only show direct price if no SKU or MAC prices are available */}
                    {catalogPrice != null && !hasSkuRates && !hasMacRates && (
                      <Typography fontSize={"16px"} fontWeight={700} color="text.primary" sx={{ whiteSpace: "nowrap" }}>
                        {priceFormatter(catalogPrice)}
                      </Typography>
                    )}
                    {hasSkuRates && (
                      <Typography
                        fontSize="16px"
                        fontWeight={700}
                        sx={{
                          color: "text.primary",
                          whiteSpace: "nowrap",
                        }}
                      >
                      $SKU Rate
                      </Typography>
                    )}
                    {hasMacRates && (
                      <Typography
                        fontSize="16px"
                        fontWeight={700}
                        sx={{
                          color: "text.primary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        $MAC Rate
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
                
              {/* Extended Price Price Column (Only when extendedPrice exists) */}
              {isAgreementFinished && extendedPrice != null && (
                <Box display="flex" flexDirection="column" alignItems="center" sx={{ 
                  width: { xs: 60, sm: 80, md: 100, lg: 120 }, 
                  flexShrink: 1 
                }}>
                  <Typography fontSize="10px" sx={{ color: "#8A99A8", mb: 0.2, fontWeight: 500, whiteSpace: "nowrap" }}>
                    Your Price
                  </Typography>
                  {loading ? (
                    <Skeleton width={40} height={20} />
                  ) : (
                    <Typography fontSize={"16px"} fontWeight={700} color="text.primary" sx={{ whiteSpace: "nowrap" }}>
                      {priceFormatter(extendedPrice)}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Desktop Action Button Column */}
          {!isMobile && (
            <Box sx={{ 
              width: listActionButtonWidth, 
              display: "flex", 
              justifyContent: "center",
              flexShrink: 0
            }}>
              {showAgreementButton ? (
                renderAgreementButton(true)
              ) : showOrderButton ? (
                renderOrderButton("Order")
              ) : shouldShowRequestRx ? (
                renderComingSoonButton("Request Rx")
              ) : null}
            </Box>
          )}

          {/* CS-1728: View Documents button (list view) */}
          {!loading && (
            <CustomTooltip title="View product documents" placement="top">
              <Button
                size="small"
                startIcon={<FileText size={14} />}
                onClick={handleViewDocuments}
                sx={{
                  ...secondaryActionBtnSx,
                  ...compactBtnSx,
                  flexShrink: 0,
                  width: listActionButtonWidth,
                }}
              >
                Docs
              </Button>
            </CustomTooltip>
          )}

          {!loading && shouldShowRequestRx && (
            <CustomTooltip title="View pharmacy ship-to coverage" placement="top">
              <IconButton
                size="small"
                onClick={handleViewCoverage}
                sx={{
                  flexShrink: 0,
                  borderRadius: "8px",
                  minHeight: { xs: 30, md: 36 },
                  color: isDark ? "#2dd4bf" : "#0d9488",
                  border: `1px solid ${isDark ? "rgba(45,212,191,0.4)" : "rgba(13,148,136,0.4)"}`,
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(45,212,191,0.1)" : "rgba(13,148,136,0.08)",
                    borderColor: isDark ? "#2dd4bf" : "#0d9488",
                  },
                }}
              >
                <Map size={16} />
              </IconButton>
            </CustomTooltip>
          )}

          {/* Favorite */}
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", width: 30, justifyContent: "center" }}>
            {loading ? (
              <Skeleton variant="circular" width={20} height={20} />
            ) : (
              <CustomTooltip title={favoriteTooltipTitle} placement="top">
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <Heart
                    size={18}
                    onClick={handleFavorite}
                    fill={isFavorite ? "#ef4444" : "transparent"}
                    color={isFavorite ? "#ef4444" : "#8A99A8"}
                    style={{ cursor: "pointer" }}
                  />
                </Box>
              </CustomTooltip>
            )}
          </Box>
        </Box>
      </CustomCard>
      {/* CS-1728: Product Documents modal (list view) */}
      {showDocumentsModal && (
        <ProductDocumentsModal
          open={showDocumentsModal}
          onClose={() => { setShowDocumentsModal(false); setPrefetchedDocs(undefined); }}
          productId={Number(id)}
          productName={title}
          initialDocs={prefetchedDocs}
        />
      )}
      </>
    );
  }

  // ─── GRID VIEW (DEFAULT) ──────────────────────────────────────────────────────
  return (
    <>
      {popups}
      <CustomCard
        onClick={handleCardClick}
        sx={{
          width: "100%",
          minHeight: 280,
          borderRadius: "16px",
          padding: "18px !important",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          cursor: disabled && !loading ? "not-allowed" : "pointer",
          border: `1px solid ${borderColor}`,
          backgroundColor: cardBg,
          backgroundImage: "none",
          backdropFilter: "blur(10px)",
          boxShadow: restShadow,
          opacity: disabled && !loading ? 0.8 : 1,
          transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: disabled ? restShadow : hoverShadow,
            borderColor: disabled ? borderColor : hoverBorderColor,
            transform: disabled ? "none" : "translateY(-3px)",
          },
        }}
      >
        {/* Favorite Heart - absolute top-right */}
        {!loading && (
          <CustomTooltip title={favoriteTooltipTitle} placement="top">
            <Box
              component="span"
              onClick={handleFavorite}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1,
                width: 30,
                height: 30,
                borderRadius: "9px",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
                },
              }}
            >
              <Heart
                size={17}
                fill={isFavorite ? "#ef4444" : "transparent"}
                color={isFavorite ? "#ef4444" : "#8A99A8"}
              />
            </Box>
          </CustomTooltip>
        )}

        {/* Tags Stack */}
        <Box
          sx={{
            display: "grid",
            gridAutoFlow: "row",
            gridTemplateColumns: "minmax(0, max-content)",
            rowGap: 0.8,
            justifyItems: "start",
            pr: 3,
          }}
        >
          {loading ? (
            <Skeleton width={80} height={24} />
          ) : composition ? (
            <CustomTooltip title={composition} placement="top" arrow>
              <Box
                sx={{
                  px: 1.2,
                  py: 0.4,
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  backgroundColor: isDark ? "rgba(20,184,166,0.15)" : "rgba(20,184,166,0.1)",
                  color: "#14b8a6",
                  border: `1px solid ${isDark ? "rgba(20,184,166,0.25)" : "rgba(20,184,166,0.2)"}`,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                  width: "fit-content",
                  display: "block"
                }}
              >
                {composition}
              </Box>
            </CustomTooltip>
          ) : null}

          {!loading && (
            <>
              {(() => {
                const chip = getAgreementChip();
                if (!chip) return null;
                return (
                  <CustomTooltip title={chip.label} placement="top" arrow>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: isDark
                          ? `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.15)`
                          : `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.1)`,
                        color: chip.color,
                        border: `1px solid ${isDark
                          ? `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.25)`
                          : `rgba(${chip.color === "#22c55e" ? "34,197,94" : "245,158,11"}, 0.2)`}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {React.cloneElement(chip.icon as React.ReactElement, { size: 14, style: { marginRight: 4 } })}
                      {chip.label}
                    </Box>
                  </CustomTooltip>
                );
              })()}

            </>
          )}
        </Box>

        {/* Product Title & Company */}
        <Box mt={1}>
          {loading ? (
            <Skeleton width="70%" height={28} />
          ) : (
            <Box display="flex" alignItems="center" gap={1} sx={{ width: "100%", overflow: "hidden" }}>
              <Typography
                fontSize={"16px"}
                fontWeight={600}
                color="text.primary"
                noWrap
                sx={{ flex: 1, minWidth: 0, textOverflow: "ellipsis", overflow: "hidden" }}
              >
                {title}
              </Typography>
              {/* Removed redundant APPROVED chip as it's now handled by getAgreementChip in the tags row */}
            </Box>
          )}

          {loading ? (
            <Skeleton width="50%" height={20} />
          ) : (
            <Typography
              fontSize={"12px"}
              sx={{ color: "#8A99A8" }}
              noWrap
            >
              {company}
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: borderColor, my: 1.5 }} />

        {/* Price section */}
        <Box display="flex" flexDirection="column" width="100%" mb={1.5}>
          {loading ? (
            <Skeleton width="75%" height={22} />
          ) : (
            <>
              {/* Left: reference price + Your Price stacked */}
              <Box display="flex" flexDirection="column" gap={0.4} width="100%">
                {(catalogPrice != null || hasSkuRates || hasMacRates) && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography fontSize="11px" sx={{ color: "#8A99A8", fontWeight: 500 }}>
                      {referencePriceLabel}:
                    </Typography>
                    {catalogPrice != null && !hasSkuRates && !hasMacRates && (
                      <Typography fontSize="13px" fontWeight={700} color="text.primary">
                        {priceFormatter(catalogPrice)}
                      </Typography>
                    )}
                    {hasSkuRates && (
                      <Typography fontSize="13px" fontWeight={700} color="text.primary">
                        $SKU Rate
                      </Typography>
                    )}
                    {hasMacRates && (
                      <Typography fontSize="13px" fontWeight={700} color="text.primary">
                        $MAC Rate
                      </Typography>
                    )}
                  </Box>
                )}
                {isAgreementFinished && extendedPrice != null && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography fontSize="11px" sx={{ color: "#8A99A8", fontWeight: 500 }}>
                      Your Price:
                    </Typography>
                    <Typography fontSize="13px" fontWeight={700} color="text.primary">
                      {priceFormatter(extendedPrice)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Grid Action Footer */}
        {showAgreementButton ? (
          <CustomTooltip title={agreementTooltipTitle} placement="top">
            <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
              <Button
                startIcon={<FileText size={18} />}
                fullWidth
                disabled={isAgreementDisabled}
                onClick={handleAgreement}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  color: agreementBtnColor,
                  backgroundColor: isDark ? `${agreementBtnColor}22` : `${agreementBtnColor}14`,
                  border: isAgreementDisabled ? "1px solid #8A99A8" : `1px solid ${agreementBtnColor}66`,
                  transition: "background-color 0.18s ease, box-shadow 0.18s ease",
                  "&:hover": {
                    backgroundColor: agreementBtnColor,
                    color: "#fff",
                    boxShadow: `0 6px 16px ${agreementBtnColor}40`,
                  },
                }}
              >
                {getAgreementLabel()}
              </Button>
            </Box>
          </CustomTooltip>
        ) : showOrderButton ? (
          <CustomTooltip title={orderTooltipTitle} placement="top">
            <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
              <Button
                startIcon={<ShoppingCart size={18} />}
                fullWidth
                disabled={disabled}
                onClick={handleOrder}
                sx={primaryActionBtnSx}
              >
                Order Product
              </Button>
            </Box>
          </CustomTooltip>
        ) : shouldShowRequestRx ? (
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, width: "100%" }}>
            <CustomTooltip title={comingSoonTooltipTitle} placement="top">
              <Box component="span" sx={{ display: "inline-flex", flex: 1, minWidth: 0 }}>
                <Button
                  startIcon={<ClipboardPlus size={18} />}
                  fullWidth
                  disabled={disabled}
                  onClick={handleOrder}
                  sx={primaryActionBtnSx}
                >
                  Request Rx
                </Button>
              </Box>
            </CustomTooltip>
            <CustomTooltip title="View pharmacy ship-to coverage" placement="top">
              <IconButton
                onClick={handleViewCoverage}
                sx={{ ...secondaryActionBtnSx, flexShrink: 0 }}
              >
                <Map size={18} />
              </IconButton>
            </CustomTooltip>
          </Box>
        ) : null}

        {/* CS-1728: View Documents button */}
        <CustomTooltip title="View product documents" placement="top">
          <Button
            fullWidth
            startIcon={<FileText size={16} />}
            onClick={handleViewDocuments}
            sx={{ ...secondaryActionBtnSx, mt: 1 }}
          >
            View Documents
          </Button>
        </CustomTooltip>
      </CustomCard>
      {/* CS-1728: Product Documents modal */}
      {showDocumentsModal && (
        <ProductDocumentsModal
          open={showDocumentsModal}
          onClose={() => { setShowDocumentsModal(false); setPrefetchedDocs(undefined); }}
          productId={Number(id)}
          productName={title}
          initialDocs={prefetchedDocs}
        />
      )}
    </>
  );
};

export default React.memo(MarketCard);

